import { useContext, useEffect, useState } from 'react';
import { TemplateContext } from './Context';
import {
  createUserPacketTemplate,
  deleteUserPacketTemplate,
  getUserPacketTemplates,
  updateUserPacketTemplate,
  updateUserPacketTemplateFromPacket,
} from '../../../services/UserPacketTemplateService';
import { useUser } from '@clerk/react';
import isEqual from 'lodash/isEqual';
import { applyTemplateToPacket } from '../../../services/PacketService';
import { useNavigate } from 'react-router-dom';
import ProjectContext from '../../Project/Contexts/ProjectContext';
import { usePacketContext } from './PacketContext';
import { instantiateRepeatingRows } from './packetContextUtils';

export const getBlockNamesFromTemplate = (template) => {
  return template.blocks.map((block) => block.title).join(', ');
};

export const useTemplateContext = () => {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error('useTemplateContext must be used within a TemplateProvider');
  }
  return context;
};

export const TemplateProvider = ({ children }) => {
  // on initial load, get the user's templates
  const [userTemplates, setUserTemplates] = useState([]);
  const { user } = useUser();
  const navigate = useNavigate();
  const projectContext = useContext(ProjectContext);
  const projectId = projectContext?.getProjectId?.();
  const { applyPacketUpdate } = usePacketContext();
  useEffect(() => {
    const loadUserTemplates = async () => {
      if (!user) {
        return;
      }
      const templates = await getUserPacketTemplates({});
      setUserTemplates(templates.data.results);
    };
    loadUserTemplates();
  }, [user]);

  const getUserTemplates = () => {
    return userTemplates;
  };

  const getTemplateById = (templateId) => {
    return userTemplates?.find((template) => template.id === templateId) || null;
  };

  const templateHasDivergedFromPacket = (packet, templateId) => {
    if (!templateId || templateId !== packet.template_id) {
      return false;
    }
    const template = getTemplateById(templateId);

    if (!template) {
      return false;
    }

    const blocks = packet.blocks;

    // first, check if the settings have changed
    const settingsOnPacket = packet.settings;
    const settingsInTemplate = template.packet_settings;
    if (!isEqual(settingsOnPacket, settingsInTemplate)) {
      return true;
    }

    // first, check if the canonical_id of blocks is the same as template.blocks
    const canonicalIdsInBlocks = blocks.map((block) => block.canonical_id);
    const canonicalIdsInTemplate = template.blocks.map((block) => block.canonical_id);
    if (!isEqual(canonicalIdsInBlocks, canonicalIdsInTemplate)) {
      return true;
    }

    // the blocks are the same; now we need to check if the template_data is the same
    const templateDataInBlocks = blocks.map((block) => block.template_data);
    const templateDataInTemplate = template.blocks.map((block) => block.template_data);
    if (!isEqual(templateDataInBlocks, templateDataInTemplate)) {
      return true;
    }

    // if all that is the same, let's go through and check if the resource_requirements have changed
    let hasDiverged = false;
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      const resourceRequirementsForBlock = {};
      if (block.resource_data) {
        const types = Object.keys(block.resource_data);
        types.forEach((type) => {
          resourceRequirementsForBlock[type] = {
            count: { default: block.resource_data[type].items.length },
          };
        });
      }
      const blockInTemplate = template.blocks[i];
      if (!isEqual(resourceRequirementsForBlock, blockInTemplate.default_data_requirements)) {
        console.log(
          `resource requirements have diverged for ${block.canonical_id}`,
          resourceRequirementsForBlock,
          blockInTemplate.default_data_requirements
        );
        hasDiverged = true;
      }
    }
    return hasDiverged;
  };

  const updateTemplateFromPacket = async (packetId, templateId) => {
    const response = await updateUserPacketTemplateFromPacket(templateId, packetId);
    const newTemplate = response.data.results;
    const newTemplates = userTemplates.map((template) =>
      template.id === newTemplate.id ? newTemplate : template
    );
    setUserTemplates(newTemplates);
    return newTemplate;
  };

  const updatePacketFromTemplate = async (packetId, templateId) => {
    const response = await applyTemplateToPacket(templateId, packetId, projectId);
    sessionStorage.setItem('didApplyTemplateToPacket', true);
    if (projectId && response.data.packet_data) {
      // Project mode: apply locally for undo support (server computed but didn't save)
      const packetData = response.data.packet_data;
      packetData.blocks?.forEach(instantiateRepeatingRows);
      applyPacketUpdate((prevPacket) => ({
        ...prevPacket,
        ...packetData,
      }));
    } else {
      navigate(`/p/${response.data.packet_id}`);
    }
  };

  const getTemplatesByOrientation = (orientation) => {
    return userTemplates?.filter((template) => template.orientation === orientation) || [];
  };

  const createTemplateFromPacket = async (packetId, templateName) => {
    const newTemplate = await createUserPacketTemplate(templateName, packetId);
    const newTemplateData = newTemplate.data.results;
    // filter out the new template from the user templates
    const newTemplates = userTemplates.filter((template) => template.id !== newTemplateData.id);
    setUserTemplates([...newTemplates, newTemplateData]);
    return newTemplateData;
  };

  const deleteTemplate = async (templateId) => {
    await deleteUserPacketTemplate(templateId);
    setUserTemplates(userTemplates.filter((template) => template.id !== templateId));
  };

  const renameTemplate = async (templateId, newName) => {
    const response = await updateUserPacketTemplate(templateId, { title: newName });
    const newTemplates = userTemplates.map((template) =>
      template.id === response.data.results.id ? response.data.results : template
    );
    setUserTemplates(newTemplates);
    return response.data.results;
  };

  const updateTemplate = async (templateId, data) => {
    const response = await updateUserPacketTemplate(templateId, data);
    const newTemplates = userTemplates.map((template) =>
      template.id === response.data.results.id ? response.data.results : template
    );
    setUserTemplates(newTemplates);
    return response.data.results;
  };

  // Update a template in the local state (used when API call is made elsewhere)
  const syncTemplateInList = (updatedTemplate) => {
    const newTemplates = userTemplates.map((template) =>
      template.id === updatedTemplate.id ? updatedTemplate : template
    );
    setUserTemplates(newTemplates);
  };

  return (
    <TemplateContext.Provider
      value={{
        getUserTemplates,
        createTemplateFromPacket,
        deleteTemplate,
        getTemplateById,
        getTemplatesByOrientation,
        templateHasDivergedFromPacket,
        updateTemplateFromPacket,
        updatePacketFromTemplate,
        renameTemplate,
        updateTemplate,
        syncTemplateInList,
      }}>
      {children}
    </TemplateContext.Provider>
  );
};
